import React, { useState, ChangeEvent, useEffect, useImperativeHandle, Ref } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useGetManufacture } from '../../hooks/useGetManufacture';
import Preloader from '../../../../../components/other/Preloader/Preloader';
import { useEditManufacture } from '../../hooks/useEditManufacture';
import { useAddManufacture } from '../../hooks/useAddManufacture';

interface ManufacturerForm {
  manufacturer: string;
  manufacturer_print: string;
  english_name: string;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 16,
    padding: theme.spacing(1),
    minWidth: 500,
  },
}));

const StyledTitle = styled(DialogTitle)(({ theme }) => ({
  fontSize: 22,
  fontWeight: 700,
  paddingBottom: theme.spacing(1),
}));

const StyledTextField = styled(TextField)(() => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#fafafa',
  },
}));

export type ModalMethods = {
  onOpen: () => void;
} | null;

const ManufacturerModal = ({
  id,
  update,
  onClose,
  modalRef,
}: {
  id?: number;
  update?: () => void;
  onClose?: () => void;
  modalRef?: Ref<ModalMethods>;
}) => {
  const {
    handleManufacture: getManufacturer,
    loading,
    manufacture,
    clear,
  } = useGetManufacture();
  const {
    handleManufacture: editManufacture,
    loading: editLoading,
    success: editSuccess,
  } = useEditManufacture();

  const { addManufacture, loading: addLoading, success: addSuccess } = useAddManufacture();
  const [open, setOpen] = useState<boolean>(false);

  const [form, setForm] = useState<ManufacturerForm>({
    manufacturer: '',
    manufacturer_print: '',
    english_name: '',
  });

  useImperativeHandle(modalRef, () => ({
    onOpen: () => {
      setTimeout(() => {
          setOpen(true);
      }, 100)
    },
  }));

  useEffect(() => {
    if (id) {
      getManufacturer(id);
    } else {
      clear();
    }
  }, [id]);

  useEffect(() => {
    if (manufacture) {
      setForm({
        manufacturer: manufacture.name || '',
        manufacturer_print: manufacture.print_name || '',
        english_name: manufacture.english_name || '',
      });
    } else {
      setForm({
        manufacturer: '',
        manufacturer_print: '',
        english_name: '',
      });
    }
  }, [manufacture]);

  const handleChange =
    (field: keyof ManufacturerForm) => (event: ChangeEvent<HTMLInputElement>) => {
      setForm(prev => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleClose = (): void => {
    setOpen(false);
    setTimeout(() => {
        onClose?.();
    }, 100);
  };

  useEffect(() => {
    if (editSuccess || addSuccess) {
      update?.();
      handleClose();
    }
  }, [editSuccess, addSuccess]);

  const handleSave = (): void => {
    if (id) {
      editManufacture(id, {
        name: form.manufacturer,
        printName: form.manufacturer_print,
        englishName: form.english_name,
      });
    } else {
      addManufacture({
        name: form.manufacturer,
        printName: form.manufacturer_print,
        englishName: form.english_name,
      });
    }
  };

  return (
    <StyledDialog open={open} onClose={handleClose}>
      {loading ? (
        <Preloader />
      ) : (
        <>
          <StyledTitle>{id ? 'Редактирование' : 'Создание'}</StyledTitle>

          <DialogContent>
            <Stack spacing={2} mt={1}>
              <StyledTextField
                label='Уникальное наименование'
                fullWidth
                variant='outlined'
                value={form.manufacturer}
                onChange={handleChange('manufacturer')}
                disabled={editLoading || addLoading}
              />

              <StyledTextField
                label='Отображаемое наименование'
                fullWidth
                variant='outlined'
                value={form.manufacturer_print}
                onChange={handleChange('manufacturer_print')}
                disabled={editLoading || addLoading}
              />

              <StyledTextField
                label='Английское наименование'
                fullWidth
                variant='outlined'
                value={form.english_name}
                onChange={handleChange('english_name')}
                disabled={editLoading || addLoading}
              />
            </Stack>
          </DialogContent>

          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleClose} variant='outlined'>
              Отмена
            </Button>

            <Button
              onClick={handleSave}
              variant='contained'
              disabled={editLoading || addLoading}
            >
              Сохранить
            </Button>
          </DialogActions>
        </>
      )}
    </StyledDialog>
  );
};

export default ManufacturerModal;
